import type { MouseEventHandler } from 'react';
import { forwardRef } from 'react';
import type { LinkValue } from '@elfsight/configurator-contracts';
import type { ButtonProps } from '@elfsight/widget-ui';
import { Button } from '@elfsight/widget-ui';
import { openWindowByLink } from '@elfsight/widget-utils';
import { useBanner } from '../../bannerProvider';
import type { BlockProps } from '../_block';
import { Block } from '../_block';

export type ButtonBlockProps = BlockProps & {
  applyMobileSettings: boolean;
  buttonProps: ButtonProps & {
    mobileFont: ButtonProps['font'];
    mobileHorizontalPadding: ButtonProps['horizontalPadding'];
    mobileVerticalPadding: ButtonProps['verticalPadding'];
  };
  link?: LinkValue;
};

export const ButtonBlock = forwardRef<HTMLDivElement, ButtonBlockProps>(
  (
    { applyMobileSettings, link, buttonProps, ...forwardingProps },
    forwardingRef
  ) => {
    const { contentDirection, trackGoogleAnalyticsEvents } = useBanner();

    const {
      mobileFont,
      mobileHorizontalPadding,
      mobileVerticalPadding,
      font,
      horizontalPadding,
      verticalPadding,
      fullWidth,
      text,
      onClick,
      ...buttonForwardingProps
    } = buttonProps;

    const isRow = contentDirection === 'row';

    const currentFont = applyMobileSettings ? mobileFont : font;
    const currentHorizontalPadding = applyMobileSettings
      ? mobileHorizontalPadding
      : horizontalPadding;
    const currentVerticalPadding = applyMobileSettings
      ? mobileVerticalPadding
      : verticalPadding;

    const handleClick: MouseEventHandler<HTMLButtonElement> = (e) => {
      if (onClick) {
        onClick(e);
      }

      if (link) {
        openWindowByLink(e, link);
      }

      if (trackGoogleAnalyticsEvents) {
        const valueMapping = {
          buttonText: text,
          buttonUrl: link?.value
        };
        trackGoogleAnalyticsEvents({
          eventType: 'buttonClicked',
          valueMapping
        });
      }
    };

    return (
      <Block
        {...forwardingProps}
        ref={forwardingRef}
        aria-label={link ? `Link by ${link.type}` : 'Button'}
      >
        <Button
          {...buttonForwardingProps}
          text={text}
          fullWidth={isRow ? false : fullWidth}
          font={currentFont}
          horizontalPadding={currentHorizontalPadding}
          verticalPadding={currentVerticalPadding}
          disableEllipsis={isRow}
          onClick={handleClick}
        />
      </Block>
    );
  }
);
